import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  discardClearingExportBatch,
  getClearingExportBatch,
  settleClearingExportBatch,
  unsettleClearing,
  ZenginExportBatchDetail,
} from '~/javascripts/admin/actions/clearing';
import Button from '../../atoms/Buttons';
import ConfirmModal from '../../atoms/ConfirmModal';
import Headings from '../../atoms/Headings';
import BreadCrumb from '../../molecules/BreadCrumb';

const PageWrapper = styled.div`
  padding-bottom: 40px;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  padding: 16px 20px;
  background: #f5f7f9;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: bold;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 8px 12px;
    border-bottom: 1px solid #e5e5e5;
    text-align: left;
  }
  th {
    background: #fafafa;
    white-space: nowrap;
  }
  td.amount {
    text-align: right;
  }
`;

const Notice = styled.div<{ tone: 'warning' | 'error' | 'info' }>`
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 13px;
  ${({ tone }) => {
    if (tone === 'error') return 'background: #fdecea; border: 1px solid #f5c6cb;';
    if (tone === 'warning') return 'background: #fff8e1; border: 1px solid #ffe082;';
    return 'background: #eef4fb; border: 1px solid #cfe0f3;';
  }}
`;

const DisabledReason = styled.div`
  align-self: center;
  color: #888;
  font-size: 12px;
`;

const ConfirmBody = styled.div`
  padding: 8px 4px 0;
  text-align: center;
`;

const ConfirmButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
`;

const yen = (amount: number) => `${amount.toLocaleString()} 円`;

const FALLBACK_CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '精算管理', href: '', isCurrentPage: true },
  { value: '振込データ履歴', href: '/clearings/export-batches', isCurrentPage: false },
  { value: '詳細', href: '', isCurrentPage: true },
];

const ClearingExportBatchDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [batch, setBatch] = useState<ZenginExportBatchDetail | null>(null);
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // 精算は不可逆なので、対象件数と合計を再掲した確認を挟む。
  const [isSettleConfirmOpen, setIsSettleConfirmOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const fetchBatch = useCallback(async () => {
    try {
      setBatch(await getClearingExportBatch(id));
    } catch {
      // 取得できないと画面が真っ白のままになり、権限が無いのか消えたのか分からない。
      setLoadError(true);
    }
  }, [id]);

  useEffect(() => {
    fetchBatch();
  }, [fetchBatch]);

  const run = async (action: () => Promise<void>) => {
    setIsProcessing(true);
    setMessage(null);
    try {
      await action();
      await fetchBatch();
    } catch (error) {
      const body = (await Promise.resolve(error).catch(() => null)) as { message?: string } | null;
      setMessage({ tone: 'error', text: body?.message ?? '処理に失敗しました' });
    } finally {
      setIsProcessing(false);
    }
  };

  const settle = () =>
    run(async () => {
      const result = await settleClearingExportBatch(id);
      setIsSettleConfirmOpen(false);
      setMessage({
        tone: 'info',
        text: `${result.settled} 件を精算済にしました（相殺で連動: ${result.cascade_resolved} 件 / 既に精算済: ${result.skipped} 件）`,
      });
    });

  const toggleDiscard = (discard: boolean) =>
    run(async () => {
      await discardClearingExportBatch(id, discard);
      setMessage({
        tone: 'info',
        text: discard
          ? 'このデータを破棄しました。未精算の警告には出てこなくなります'
          : '破棄を取り消しました',
      });
    });

  const revert = (clearingId: string) =>
    run(async () => {
      const result = await unsettleClearing(clearingId);
      setMessage({
        tone: 'info',
        text:
          result.reverted_count > 0
            ? `精算を取り消しました（相殺で連動していた請求 ${result.reverted_count} 件も未精算に戻しました）`
            : '精算を取り消しました',
      });
    });

  if (loadError) {
    return (
      <PageWrapper>
        <BreadCrumb crumbs={FALLBACK_CRUMBS} />
        <Notice tone="error">
          振込データを取得できませんでした。削除されているか、参照する権限がない可能性があります。
        </Notice>
      </PageWrapper>
    );
  }

  if (!batch) return null;

  const isDiscarded = Boolean(batch.discarded_at);
  const unsettledCount = batch.unsettled_count ?? 0;
  const settledCount = (batch.clearing_count ?? 0) - unsettledCount;

  return (
    <PageWrapper>
      <BreadCrumb
        crumbs={[
          { value: 'ホーム', href: '/', isCurrentPage: false },
          { value: '精算管理', href: '', isCurrentPage: true },
          { value: '振込データ履歴', href: '/clearings/export-batches', isCurrentPage: false },
          { value: '詳細', href: '', isCurrentPage: true },
        ]}
      />
      <Headings.h5>振込データ</Headings.h5>

      {message && <Notice tone={message.tone}>{message.text}</Notice>}

      {isDiscarded && (
        <Notice tone="warning">
          このデータは破棄されています（銀行に上げていない扱い）。未精算の警告には出てこず、
          精算済にすることもできません。取り消せば元に戻せます。
        </Notice>
      )}

      <Summary>
        <div>
          <div>作成日時</div>
          <SummaryValue>{moment(batch.created_at).format('YYYY/MM/DD HH:mm')}</SummaryValue>
        </div>
        <div>
          <div>振込指定日</div>
          <SummaryValue>
            {moment(batch.scheduled_transfer_date).format('YYYY/MM/DD')}
          </SummaryValue>
        </div>
        <div>
          <div>振込件数（加盟店単位）</div>
          <SummaryValue>{batch.transfer_count} 件</SummaryValue>
        </div>
        <div>
          <div>振込金額</div>
          <SummaryValue>{yen(batch.total_amount)}</SummaryValue>
        </div>
        <div>
          <div>精算データ</div>
          <SummaryValue>
            未精算 {unsettledCount} 件 / 精算済 {settledCount} 件
          </SummaryValue>
        </div>
      </Summary>

      <Actions>
        {batch.url && (
          <Button
            variant="neutral"
            as="a"
            href={batch.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            ファイルを再ダウンロード
          </Button>
        )}
        <Button
          disabled={isProcessing || isDiscarded || unsettledCount === 0}
          onClick={() => setIsSettleConfirmOpen(true)}
        >
          精算済にする
        </Button>
        {/* 精算済の明細を含むバッチは破棄できない（振込・精算まで済んだものを
            「振り込んでいない」ことにはできない）。サーバも422で拒否するが、
            押せてしまうと運用者は理由が分からないので活性から外す。 */}
        <Button
          variant="neutral"
          disabled={isProcessing || (!isDiscarded && settledCount > 0)}
          onClick={() => toggleDiscard(!isDiscarded)}
        >
          {isDiscarded ? '破棄を取り消す' : '破棄する'}
        </Button>
        {!isDiscarded && settledCount > 0 && (
          <DisabledReason>精算済の明細を含むため破棄できません</DisabledReason>
        )}
      </Actions>

      <Headings.h6>含まれる精算データ</Headings.h6>
      <Table>
        <thead>
          <tr>
            <th>加盟店</th>
            <th>マネー</th>
            <th>対象期間</th>
            <th>精算金額</th>
            <th>債権相殺額</th>
            <th>実振込額</th>
            <th>精算状態</th>
            <th aria-label="操作" />
          </tr>
        </thead>
        <tbody>
          {batch.clearings.map((clearing) => (
            <tr key={clearing.id}>
              <td>{clearing.claimed_organization?.name}</td>
              <td>{clearing.private_money?.name}</td>
              <td>
                {moment(clearing.from).format('YYYY/MM/DD')} 〜{' '}
                {moment(clearing.to).format('YYYY/MM/DD')}
              </td>
              <td className="amount">{yen(clearing.pre_offset_amount)}</td>
              <td className="amount">
                {clearing.offset_amount > 0 ? `- ${yen(clearing.offset_amount)}` : '—'}
              </td>
              {/* バッチの振込金額はこの列の合計。精算金額をそのまま出すと相殺分だけ
                  合計が合わず、運用者が数字を突合できない。 */}
              <td className="amount">
                {clearing.actual_transfer_amount === null
                  ? '—'
                  : yen(clearing.actual_transfer_amount)}
              </td>
              <td>{clearing.done_at ? '精算済' : '未精算'}</td>
              <td>
                {clearing.done_at && (
                  // 部分返戻(口座解約・名義相違で差し戻された分)を1件ずつ戻すための導線。
                  <Button variant="neutral" disabled={isProcessing} onClick={() => revert(clearing.id)}>
                    精算を取り消す
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <ConfirmModal
        isModalOpen={isSettleConfirmOpen}
        closeModal={() => setIsSettleConfirmOpen(false)}
        title="この振込データを精算済にします"
        form={
          <ConfirmBody>
            <p>
              この振込データ（加盟店 {batch.transfer_count} 件 / 振込金額 {yen(batch.total_amount)}）に
              含まれる精算データのうち、未精算の <strong>{unsettledCount} 件</strong>を精算済にします。
            </p>
            <p>
              精算済にすると元に戻すのは1件ずつの操作になります。銀行へのアップロードが
              完了していることを確認してください。
            </p>
            <ConfirmButtons>
              <Button variant="neutral" onClick={() => setIsSettleConfirmOpen(false)}>
                キャンセル
              </Button>
              <Button disabled={isProcessing} onClick={settle}>
                精算済にする
              </Button>
            </ConfirmButtons>
          </ConfirmBody>
        }
      />
    </PageWrapper>
  );
};

export default ClearingExportBatchDetail;
